import axios from 'axios';
import { clearAuthUser, readAuthUser } from '../utils/authSession.js';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

function isFormDataBody(data) {
  if (!data || typeof data !== 'object') return false;
  if (typeof FormData !== 'undefined' && data instanceof FormData) return true;
  return data[Symbol.toStringTag] === 'FormData' && typeof data.append === 'function';
}

function unsetContentType(headers) {
  if (!headers) return;
  // Only this request's headers — do not walk common/post defaults.
  if (typeof headers.delete === 'function') {
    headers.delete('Content-Type');
    headers.delete('content-type');
  }
  if (typeof headers.set === 'function') {
    headers.set('Content-Type', false);
  }
}

const instance = axios.create({
  baseURL: API_BASE_URL,
  // Auth is a Bearer token. Sending Chrome's cookie jar on every
  // cross-origin call can make Flask/Werkzeug reject the request with 400.
  withCredentials: false,
});

// One open page must not fire every list, settings, and session call at once.
const MAX_CONCURRENT_GETS = 4;
let activeGets = 0;
const getWaiters = [];

function takeGetSlot() {
  if (activeGets < MAX_CONCURRENT_GETS) {
    activeGets += 1;
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    getWaiters.push(resolve);
  });
}

function freeGetSlot(config) {
  if (!config || !config._getSlot) return;
  config._getSlot = false;
  const next = getWaiters.shift();
  if (next) {
    next();
    return;
  }
  activeGets = Math.max(0, activeGets - 1);
}

instance.interceptors.request.use(
  async (config) => {
    let slotted = false;
    try {
      if ((config.method || 'get').toLowerCase() === 'get') {
        await takeGetSlot();
        config._getSlot = true;
        slotted = true;
      }
      if (!config.headers) {
        config.headers = {};
      }

      const user = readAuthUser() || {};
      const token = user.token;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      if (isFormDataBody(config.data)) {
        // Chrome will not add the multipart boundary if Content-Type is already
        // set to "multipart/form-data". Let the browser set the full header.
        unsetContentType(config.headers);
      } else if (
        config.data
        && typeof config.data === 'object'
        && !config.headers['Content-Type']
        && !config.headers['content-type']
      ) {
        config.headers['Content-Type'] = 'application/json';
      }

      return config;
    } catch (error) {
      if (slotted) freeGetSlot(config);
      throw error;
    }
  },
  (error) => Promise.reject(error)
);

function isBusyError(error) {
  const status = error.response?.status;
  const message = String(error.response?.data?.error || '');
  return status === 503 || /server is busy/i.test(message);
}

instance.interceptors.response.use(
  (response) => {
    freeGetSlot(response.config);
    return response;
  },
  async (error) => {
    const config = error.config || {};
    freeGetSlot(config);
    if (error.response?.status === 401) {
      const url = config.url || '';
      const message = String(error.response?.data?.error || '');
      const poolBusy = /server is busy|token validation failed|queuepool|timed out/i.test(message);
      if (!poolBusy && !url.includes('/auth/login')) {
        clearAuthUser();
        window.location.href = '/login';
      }
    }

    if (error.response?.status === 403) {
      console.error('Access denied:', error.response.data?.error);
    }

    const method = String(config.method || 'get').toLowerCase();
    const busyAttempt = Number(config._busyRetryCount || 0);
    // Up to 6 retries (~0.3+0.6+1.2+2.4+3+3s) so a cold settlement rebuild can finish.
    if (method === 'get' && isBusyError(error) && busyAttempt < 6) {
      config._busyRetryCount = busyAttempt + 1;
      const delayMs = Math.min(3000, 300 * (2 ** busyAttempt));
      await new Promise((resolve) => setTimeout(resolve, delayMs));
      return instance.request(config);
    }

    return Promise.reject(error);
  }
);

export default instance;
